import type { Signal } from '@angular/core';
import { signal } from '@angular/core';

import { createProviderToken } from '@core/common-legacy';
import type { TranslationsRepository } from '@core/i18n-legacy';

export type TellentAssistantMobileTranslationSources = TranslationsRepository[];

export const {
  provide: provideTellentAssistantMobileTranslationSources,
  inject: injectTellentAssistantMobileTranslationSources,
} = createProviderToken<TellentAssistantMobileTranslationSources>(
  'TELLENT_ASSISTANT_MOBILE_TRANSLATION_SOURCES',
)(false);

/**
 * The locale the assistant should render in, read lazily so the host can resolve
 * it after its own i18n has initialised. Injected optionally: a host that renders
 * the assistant through its own i18n instance does not need to provide one, and
 * the fallback then sits next to the default locale it belongs with.
 */
export type TellentAssistantMobileLocale = () => string;

export const {
  provide: provideTellentAssistantMobileLocale,
  inject: injectTellentAssistantMobileLocale,
} = createProviderToken<TellentAssistantMobileLocale>('TELLENT_ASSISTANT_MOBILE_LOCALE')(false);

export interface AssistantModalHandle {
  present: () => Promise<void>;
  dismiss: (data?: unknown, role?: string) => Promise<boolean>;
  onWillDismiss: () => Promise<unknown>;
}

export interface TellentAssistantMobileModalPresenter {
  createCard: (options: {
    component: unknown;
    canDismiss?: boolean;
  }) => Promise<AssistantModalHandle>;
}

export const {
  provide: provideTellentAssistantMobileModalPresenter,
  inject: injectTellentAssistantMobileModalPresenter,
} = createProviderToken<TellentAssistantMobileModalPresenter>(
  'TELLENT_ASSISTANT_MOBILE_MODAL_PRESENTER',
)(false);

export const {
  provide: provideTellentAssistantMobileKeyboardHeight,
  inject: injectTellentAssistantMobileKeyboardHeight,
} = createProviderToken<Signal<number>>('TELLENT_ASSISTANT_MOBILE_KEYBOARD_HEIGHT')(false, {
  providedIn: 'root',
  factory: () => signal(0),
});

export const {
  provide: provideTellentAssistantMobileCompanyKey,
  inject: injectTellentAssistantMobileCompanyKey,
} = createProviderToken<Signal<string | null>>('TELLENT_ASSISTANT_MOBILE_COMPANY_KEY')(false, {
  providedIn: 'root',
  factory: () => signal(null),
});

// Cross-boundary contract for hosts (e.g. manage) whose mobile modal presenter bridges to a
// non-Angular-rendered sheet via plain `window` CustomEvents instead of an AssistantModalHandle
// that actually renders something.
export const TELLENT_ASSISTANT_MOBILE_SHEET_EVENT = <const>{
  open: 'tellent-assistant-mobile:open',
  close: 'tellent-assistant-mobile:close',
};
